import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { set, get, cloneDeep, isBoolean } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RowHeightParams } from 'ag-grid-community';
import { useProductCostingState } from '../ProductCostingContext';
import { checkCurrency, roundOff, roundingOff, restrictToTwoDecimals, checkIsNumber } from '../../../../Components/helper';
import { averageCostingFieldpath, errorpath } from '../fieldPath';
import useDetailedPageHook from '../Hooks/useDetailedPageHook';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import useValidationHooks from '../Hooks/useValidationHooks';
import BombiixAGGrid, { HierExpandCell, HierActionInputCell, ExpandChevronCell } from '../../../../Components/BombiixAGGrid';
import type { BombiixCellRendererParams, BombiixColumnDefOrGroup, BombiixFillBatchItem, BombiixRowId } from '../../../../Components/BombiixAGGrid/types';
import CostingCardShell from './CostingCardShell';
import { CORE_MAX_ROWS, gridHeight } from './costingRowHeights';
import type { AverageCostingExpandRow, AverageCostingRow, ProductCostingState } from '../types';
import { useLengthLabel } from '../../../../hooks/useLengthLabel';
import { TickIcon } from '../../../../bombiix-icon';

/** Static default-row tick, matched to the old f-16 glyph. */
const ICON_SIZE_CHECK = 16;

/** Anything the table displays by `name`, with `label` as the secondary key. */
interface NamedRef {
  name?: string;
  label?: string;
}

/** One editable quantity/price line under an expanded colourway. */
interface AvgQuantityRow {
  quantityPerSizCol?: unknown;
  pricePerUnit?: unknown;
  totalPrice?: unknown;
  isChangedBySupplierQuantityPerSizCol?: boolean;
  isChangedBySupplierPricePerUnit?: boolean;
  [key: string]: unknown;
}

/** An expanded child of an average-costing row, with the fields this table reads narrowed. */
type AvgExpandRow = AverageCostingExpandRow & {
  defaultRowIndex?: number;
  multiQuantityRows?: AvgQuantityRow[];
  size?: unknown;
  length?: NamedRef;
  colourway?: { _id?: string };
};

/** An average-costing parent row, with the fields this table reads narrowed. */
type AvgSourceRow = AverageCostingRow & {
  colourway?: NamedRef;
  sizeRange?: Array<NamedRef | string | undefined>;
  isExpanded?: boolean | undefined;
  expandData?: AvgExpandRow[];
};

/** The redux slices this table reads. */
interface AverageCostingRootState {
  newProductCostingData: { productSizes?: string[]; productCostingPreviousData?: Record<string, unknown> };
  UserDetails: { isSupplierLoggedIn?: boolean };
  BOMConfigDetails: { isProductArchived?: boolean };
}

/**
 * One rendered row. Declared with `type` rather than `interface`: BombiixAGGrid constrains its row
 * generic to `Record<string, unknown> & {…}`, and only type aliases receive the implicit index
 * signature.
 */
type AverageCostingGridRow = {
  __cagId: string;
  __rowType: 'summary' | 'data' | 'grandTotal';
  parentIndex?: number | undefined;
  chindex?: number | undefined;
  rowIndex?: number | undefined;
  __isFirst?: boolean | undefined;
  __isLast?: boolean | undefined;
  __span?: number | undefined;
  __showChevron?: boolean | undefined;
  isExpanded?: boolean | undefined;
  colourway?: string | undefined;
  size?: string | undefined;
  length?: string | undefined;
  quantity?: unknown;
  price?: string | undefined;
  total?: string | undefined;
  pricePerUnit?: unknown;
  totalPrice?: unknown;
  isDefaultRow?: boolean | undefined;
  colourwayId?: string | undefined;
  canDelete?: boolean | undefined;
  /** True once the colourway holds more than one quantity line, so a default can actually be chosen. */
  hasMultipleQuantityRows?: boolean | undefined;
  radioGroup?: string | undefined;
  priceAmber?: boolean | undefined;
  qtyAmber?: boolean | undefined;
  qtyErr?: string | undefined;
  priceErr?: string | undefined;
};

interface ExpandAllHeaderProps {
  allExpanded: boolean;
  onToggleAll: (next: boolean) => void;
}

/**
 * Expand/collapse-all chevron for this table's header.
 *
 * BombiixAGGrid ships its own ExpandAllHeader, but that one drives AG Grid master/detail nodes.
 * This table fakes its hierarchy with flat rows and synthetic markers, so expansion is owned by the
 * caller and the header has to be too.
 */
function ExpandAllHeader(props: ExpandAllHeaderProps): React.JSX.Element {
  const { allExpanded, onToggleAll } = props;
  return (
    <div className="cag-header cag-header--expand-all">
      <ExpandChevronCell
        expanded={Boolean(allExpanded)}
        onToggle={() => onToggleAll(!allExpanded)}
        expandLabel="Expand all"
        collapseLabel="Collapse all"
      />
    </div>
  );
}

/** Reads a size off either a `{ name | label }` object or a bare value. */
const sizeName = (size: unknown): string => {
  if (size && typeof size === 'object') {
    const named = size as NamedRef;
    return named?.name ?? named?.label ?? '';
  }
  return size == null ? '' : String(size);
};

type TotalPriceTextCellProps = BombiixCellRendererParams<AverageCostingGridRow> & {
  accessor?: (row: AverageCostingGridRow | undefined) => unknown;
};

/** Read-only total-price cell: plain text with a title attribute, no input chrome. */
function TotalPriceTextCell(params: TotalPriceTextCellProps): React.JSX.Element {
  const { data, accessor } = params;
  const text = typeof accessor === 'function' ? accessor(data) : '';
  return (
    <div className="cag-cell cag-cell-readonly d-flex align-items-center">
      <span title={typeof text === 'string' ? text : ''}>{text as React.ReactNode}</span>
    </div>
  );
}

type DefaultRadioCellProps = BombiixCellRendererParams<AverageCostingGridRow> & { canEdit?: boolean };

/**
 * Radio marking which quantity line of a colourway is the default; a tick when read-only.
 *
 * Renders nothing until the colourway holds more than one quantity line. The default is decided by
 * `rowIndex === defaultRowIndex`, which a lone row satisfies trivially, so every colourway used to
 * carry a marked control on a choice that does not exist — on a new costing that is every row at
 * once, which reads as a row of ticked checkboxes rather than one selected radio.
 */
function DefaultRadioCell(params: DefaultRadioCellProps): React.JSX.Element | null {
  const { data, onRowChange, getRowId, canEdit } = params;
  const isDataRow = data?.__rowType === 'data';
  if (!isDataRow || !data?.hasMultipleQuantityRows) return null;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const inputId = String(rowId ?? '');
  return (
    <div className="cag-cell d-flex align-items-center justify-content-center">
      {canEdit ? (
        <p className="custom-radio radio-new m-0">
          <input
            id={inputId}
            type="radio"
            name={data?.radioGroup}
            checked={!!data?.isDefaultRow}
            onChange={() => onRowChange?.(rowId as BombiixRowId, '__default', true)}
            aria-label="Set as default"
          />
          <label htmlFor={inputId} />
        </p>
      ) : data?.isDefaultRow ? (
        <TickIcon size={ICON_SIZE_CHECK} />
      ) : null}
    </div>
  );
}

export function AverageCostingTable(): React.JSX.Element {
  const { t } = useTranslation('common');
  const lengthDisplayLabel = useLengthLabel('Length');
  const { productid } = useParams<{ productid?: string }>();

  const {
    productCostingstate,
    setProductCostingState,
    colSizeLengthToggleState,
    isAllExpandedState,
    setExpandAllState,
    globalError,
    CoastingAddEditRights,
    initialOptionsState,
  } = useProductCostingState();
  const { colourwayOptionsArr, sizeOptionsArr } = initialOptionsState;
  const {
    averageCosting: { averageCostingActive, averageCostingInfo, overAllSumOfQuantityPerSizCol, overAllSumOfTotalPrice, totLength, totSize, totColourways },
    productInfo: { currency },
    isGlobalUrl,
    isBreakdown,
    costingSizes,
  } = productCostingstate;
  const { colourWayToggle, sizeLengthToggle } = colSizeLengthToggleState;
  const { averageCostingAllExpand } = isAllExpandedState;
  const { averageCosting } = globalError;

  const {
    newProductCostingData: { productSizes, productCostingPreviousData },
    UserDetails: { isSupplierLoggedIn },
    BOMConfigDetails: { isProductArchived },
  } = useSelector((state: AverageCostingRootState) => state);

  const { handleAverageCosting, calculateAvrageCosting } = useDetailedPageHook();
  const { statusState, controlAmberColour, hideLengthCol, hideColourCol, hideSizeCol, onLoadDisable } = useShowHideSectionHooks();
  const { onChangeCheckIfErrorExist } = useValidationHooks();

  const canEdit = !statusState && !isProductArchived && CoastingAddEditRights && !onLoadDisable;

  const stateRef = useRef<ProductCostingState>(productCostingstate);
  stateRef.current = productCostingstate;

  useEffect(() => {
    if (!onLoadDisable && averageCostingActive && !productid && !isGlobalUrl) {
      handleAverageCosting(colourWayToggle, sizeLengthToggle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLoadDisable, averageCostingActive, productid, colourWayToggle, colourwayOptionsArr, sizeOptionsArr]);

  const expandAverageCostingChild = (index: number | null, expandAll?: boolean): void => {
    setProductCostingState((state) => {
      const prevInfo: AvgSourceRow[] = (state?.averageCosting?.averageCostingInfo as AvgSourceRow[]) || [];
      let nextInfo: AvgSourceRow[];
      if (isBoolean(expandAll)) {
        nextInfo = prevInfo.map((val) => (val.isExpanded === expandAll ? val : { ...val, isExpanded: expandAll }));
      } else if ((index ?? -1) >= 0) {
        nextInfo = prevInfo.map((val, rowIdx) => (rowIdx === index ? { ...val, isExpanded: !val.isExpanded } : val));
      } else {
        return state;
      }
      return { ...state, averageCosting: { ...state.averageCosting, averageCostingInfo: nextInfo } };
    });
    if (isBoolean(expandAll)) {
      setExpandAllState((prev) => ({ ...prev, averageCostingAllExpand: expandAll as boolean }));
    } else if (averageCostingAllExpand) {
      setExpandAllState((prev) => ({ ...prev, averageCostingAllExpand: false }));
    }
  };

  const mqPath = (index?: number, chindex?: number): string => averageCostingFieldpath(index, chindex).MULTI_QUANTITY_ROWS;
  const defaultIdxPath = (index?: number, chindex?: number): string => `averageCosting.averageCostingInfo[${index}].expandData[${chindex}].defaultRowIndex`;
  const qtyPath = (index?: number, chindex?: number, rowIndex?: number): string =>
    `averageCosting.averageCostingInfo[${index}].expandData[${chindex}].multiQuantityRows[${rowIndex}].quantityPerSizCol`;
  const pricePath = (index?: number, chindex?: number, rowIndex?: number): string =>
    `averageCosting.averageCostingInfo[${index}].expandData[${chindex}].multiQuantityRows[${rowIndex}].pricePerUnit`;
  const qtyFlagPath = (index?: number, chindex?: number, rowIndex?: number): string =>
    `averageCosting.averageCostingInfo[${index}].expandData[${chindex}].multiQuantityRows[${rowIndex}].isChangedBySupplierQuantityPerSizCol`;
  const priceFlagPath = (index?: number, chindex?: number, rowIndex?: number): string =>
    `averageCosting.averageCostingInfo[${index}].expandData[${chindex}].multiQuantityRows[${rowIndex}].isChangedBySupplierPricePerUnit`;

  const handleInputChange = (value: unknown, path: string, supplierPath: string | undefined, colourwayId?: string): void => {
    const copyState: ProductCostingState = cloneDeep(stateRef.current);
    set(copyState, path, value);
    if (supplierPath && (isSupplierLoggedIn || isGlobalUrl)) {
      const previousValue = get(cloneDeep(productCostingPreviousData), path);
      set(copyState, supplierPath, previousValue !== value);
    }
    calculateAvrageCosting(copyState, colourwayId);
  };

  const handleFillCells = (changes: BombiixFillBatchItem<AverageCostingGridRow>[]): void => {
    if (!Array.isArray(changes) || changes.length === 0) return;
    const copyState: ProductCostingState = cloneDeep(stateRef.current);
    const previousData: Record<string, unknown> | null = isSupplierLoggedIn || isGlobalUrl ? cloneDeep(productCostingPreviousData) : null;
    const touchedColourwayIds = new Set<string>();

    changes.forEach((change) => {
      const row = rowData.find((item) => item.__cagId === change.rowId);
      if (!row || row.__rowType !== 'data') return;
      const { parentIndex, chindex, rowIndex, colourwayId } = row;

      const restricted = restrictToTwoDecimals(change.newValue);
      if (!checkIsNumber(restricted || 0)) return;

      let valuePath: string;
      let supplierPath: string;
      if (change.field === 'quantity') {
        valuePath = qtyPath(parentIndex, chindex, rowIndex);
        supplierPath = qtyFlagPath(parentIndex, chindex, rowIndex);
      } else if (change.field === 'pricePerUnit') {
        valuePath = pricePath(parentIndex, chindex, rowIndex);
        supplierPath = priceFlagPath(parentIndex, chindex, rowIndex);
      } else {
        return;
      }

      set(copyState, valuePath, restricted);
      if (previousData) {
        set(copyState, supplierPath, get(previousData, valuePath) !== restricted);
      }
      if (colourwayId) touchedColourwayIds.add(colourwayId);
    });

    const sharedColourwayId = touchedColourwayIds.size === 1 ? [...touchedColourwayIds][0] : undefined;
    calculateAvrageCosting(copyState, sharedColourwayId);
  };

  const handleAddRow = (index?: number, chindex?: number, colourwayId?: string): void => {
    const copyState: ProductCostingState = cloneDeep(stateRef.current);
    const currentRows: AvgQuantityRow[] = get(copyState, mqPath(index, chindex), []);
    set(copyState, mqPath(index, chindex), [
      ...currentRows,
      { quantityPerSizCol: '', pricePerUnit: '', totalPrice: '', isChangedBySupplierQuantityPerSizCol: false, isChangedBySupplierPricePerUnit: false },
    ]);
    calculateAvrageCosting(copyState, colourwayId);
  };

  const handleDeleteRow = (index?: number, chindex?: number, rowIndex?: number, colourwayId?: string): void => {
    const copyState: ProductCostingState = cloneDeep(stateRef.current);
    const currentRows: AvgQuantityRow[] = get(copyState, mqPath(index, chindex), []);
    const updated = currentRows.filter((_, i) => i !== rowIndex);
    const currentDefault: number = get(copyState, defaultIdxPath(index, chindex), 0);
    set(copyState, mqPath(index, chindex), updated);
    if (currentDefault >= updated.length) {
      set(copyState, defaultIdxPath(index, chindex), Math.max(0, updated.length - 1));
    }
    calculateAvrageCosting(copyState, colourwayId);
  };

  const handleSetDefault = (index?: number, chindex?: number, rowIndex?: number, colourwayId?: string): void => {
    const copyState: ProductCostingState = cloneDeep(stateRef.current);
    set(copyState, defaultIdxPath(index, chindex), rowIndex);
    calculateAvrageCosting(copyState, colourwayId);
  };

  const updateError = (path: string): void => onChangeCheckIfErrorExist(path, globalError);

  const shouldShowExpand = hideColourCol || hideLengthCol || hideSizeCol;

  const rowData = useMemo<AverageCostingGridRow[]>(() => {
    const rows: AverageCostingGridRow[] = [];
    const list: AvgSourceRow[] = Array.isArray(averageCostingInfo) ? (averageCostingInfo as AvgSourceRow[]) : [];

    list.forEach((data, index) => {
      const expandData = data?.expandData || [];
      const isCollapsed = !data?.isExpanded && expandData.length !== 1;

      if (isCollapsed) {
        let sizeDisp = '';
        if (!colourWayToggle && !sizeLengthToggle && !hideSizeCol) sizeDisp = (productSizes || []).map(sizeName).join(', ');
        else if (colourWayToggle) sizeDisp = Array.isArray(totSize) ? totSize.map(sizeName).join(', ') : '';
        else sizeDisp = sizeName((data?.sizeRange?.[0] as NamedRef | undefined)?.name) || sizeName(data?.sizeRange?.[0]) || '';

        rows.push({
          __cagId: `p${index}-sum`,
          __rowType: 'summary',
          parentIndex: index,
          __showChevron: true,
          isExpanded: false,
          colourway: data?.colourway?.name,
          size: sizeDisp,
          length: Array.isArray(totLength) ? totLength.join(', ') : '',
          quantity: roundOff(data?.sumOfQuantityPerSizCol, 2, true),
          price: '--',
          total: `${checkCurrency(currency, data?.sumTotalPrice) || ''} ${roundingOff(data?.sumTotalPrice, 2)}`.trim(),
        });
      } else {
        const expandArr = !isBreakdown ? expandData : [expandData[0]];
        (expandArr || []).forEach((child, chindex) => {
          const defaultRowIndex = child?.defaultRowIndex ?? 0;
          const allRows: AvgQuantityRow[] = child?.multiQuantityRows?.length
            ? child.multiQuantityRows
            : [{ quantityPerSizCol: '', pricePerUnit: '', totalPrice: '' }];

          allRows.forEach((qRow, rowIndex) => {
            const isFirstRow = rowIndex === 0;
            const isDefaultRow = rowIndex === defaultRowIndex;

            let sizeDisp = '';
            if (isFirstRow) {
              if (!colourWayToggle && !sizeLengthToggle) {
                const sizeList = costingSizes?.length > 0 ? costingSizes : productSizes;
                sizeDisp = (sizeList || []).map(sizeName).join(', ');
              } else if (hideSizeCol) {
                sizeDisp = sizeName(child?.size);
              }
            }

            rows.push({
              __cagId: `p${index}-c${chindex}-r${rowIndex}`,
              __rowType: 'data',
              parentIndex: index,
              chindex,
              rowIndex,
              __isFirst: isFirstRow,
              __isLast: rowIndex === allRows.length - 1,
              __span: allRows.length,
              __showChevron: chindex === 0 && isFirstRow && !isBreakdown && expandData.length !== 1,
              isExpanded: data?.isExpanded,
              colourway: isFirstRow ? data?.colourway?.name : '',
              size: sizeDisp,
              length: isFirstRow ? child?.length?.name || '' : '',
              quantity: qRow?.quantityPerSizCol,
              pricePerUnit: qRow?.pricePerUnit,
              totalPrice: qRow?.totalPrice,
              isDefaultRow,
              colourwayId: child?.colourway?._id,
              canDelete: allRows.length > 1,
              hasMultipleQuantityRows: allRows.length > 1,
              radioGroup: `default-row-${index}-${chindex}`,
              priceAmber: qRow?.isChangedBySupplierPricePerUnit && controlAmberColour,
              qtyAmber: qRow?.isChangedBySupplierQuantityPerSizCol && controlAmberColour,
              qtyErr: get(averageCosting, `[${index}][${chindex}][${rowIndex}].quantityPerSizCol`, ''),
              priceErr: isDefaultRow ? get(averageCosting, `[${index}][${chindex}].pricePerUnit`, '') : '',
            });
          });
        });
      }
    });

    if (colourWayToggle || sizeLengthToggle) {
      const colourwayLabel = (totColourways?.length ?? 0) > 1 ? `${totColourways.length} Colourway(s)` : `${totColourways?.length || 0} Colourway`;
      const sizeLabel = (totSize?.length ?? 0) > 1 ? `${totSize.length} Size(s)` : `${totSize?.length || 0} Size`;
      const lengthLabel =
        (totLength?.length ?? 0) > 1 ? `${totLength.length} ${lengthDisplayLabel}(s)` : `${totLength?.length || 0} ${lengthDisplayLabel}`;
      rows.push({
        __cagId: 'overall-total',
        __rowType: 'grandTotal',
        colourway: hideColourCol ? colourwayLabel : '',
        size: hideSizeCol ? sizeLabel : '',
        length: hideLengthCol ? lengthLabel : '',
        quantity: `${roundOff(overAllSumOfQuantityPerSizCol, 2, true)} Units`,
        price: '--',
        total: `${checkCurrency(currency, overAllSumOfTotalPrice) || ''} ${roundingOff(overAllSumOfTotalPrice, 2)}`.trim(),
      });
    }
    return rows;
  }, [
    averageCostingInfo,
    isBreakdown,
    colourWayToggle,
    sizeLengthToggle,
    hideSizeCol,
    hideColourCol,
    hideLengthCol,
    productSizes,
    costingSizes,
    totSize,
    totLength,
    totColourways,
    overAllSumOfQuantityPerSizCol,
    overAllSumOfTotalPrice,
    currency,
    controlAmberColour,
    averageCosting,
    lengthDisplayLabel,
  ]);

  const hasSizeData = useMemo(() => rowData.some((row) => row.__rowType !== 'grandTotal' && String(row.size ?? '').trim() !== ''), [rowData]);
  const showSizeCol = ((!colourWayToggle && !sizeLengthToggle) || hideSizeCol) && hasSizeData;

  const onRowChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      const row = rowData.find((item) => item.__cagId === rowId);
      if (!row || row.__rowType !== 'data') return;
      const { parentIndex, chindex, rowIndex, colourwayId, isDefaultRow } = row;

      if (field === 'quantity') {
        const restricted = restrictToTwoDecimals(value);
        if (!checkIsNumber(restricted || 0)) return;
        handleInputChange(restricted, qtyPath(parentIndex, chindex, rowIndex), qtyFlagPath(parentIndex, chindex, rowIndex), colourwayId);
        if (get(averageCosting, `[${parentIndex}][${chindex}][${rowIndex}].quantityPerSizCol`, '')) {
          updateError(errorpath(parentIndex, chindex, rowIndex).AVERAGE.ERROR_QUANTITY);
        }
        return;
      }

      if (field === 'pricePerUnit') {
        const restricted = restrictToTwoDecimals(value);
        if (!checkIsNumber(restricted || 0)) return;
        handleInputChange(restricted, pricePath(parentIndex, chindex, rowIndex), priceFlagPath(parentIndex, chindex, rowIndex), colourwayId);
        if (isDefaultRow && get(averageCosting, `[${parentIndex}][${chindex}].pricePerUnit`, '')) {
          updateError(errorpath(parentIndex, chindex).AVERAGE.ERROR_PRICE_PER_UNIT);
        }
        return;
      }

      if (field === '__default') {
        handleSetDefault(parentIndex, chindex, rowIndex, colourwayId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowData, averageCosting],
  );

  const columnDefs = useMemo<BombiixColumnDefOrGroup<AverageCostingGridRow>[]>(() => {
    const cols: BombiixColumnDefOrGroup<AverageCostingGridRow>[] = [];

    if (shouldShowExpand) {
      cols.push({
        colId: '__expand',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        sortable: false,
        resizable: false,
        flex: 1,
        suppressHeaderMenuButton: true,
        cellRenderer: HierExpandCell,
        cellProps: {
          isParent: (row: AverageCostingGridRow) => row.__showChevron,
          getExpanded: (row: AverageCostingGridRow) => row.isExpanded,
          onToggle: (row: AverageCostingGridRow) => expandAverageCostingChild(row.parentIndex ?? null),
        },
        headerComponent: ExpandAllHeader,
        headerComponentParams: { allExpanded: averageCostingAllExpand, onToggleAll: (val: boolean) => expandAverageCostingChild(null, val) },
      });
    }

    if (hideColourCol) {
      cols.push({
        field: 'colourway',
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.AVERAGE_COSTING.COLOURWAY'),
        cellType: 'readonlyText',
        width: 150,
        sortable: false,
        flex: 1,
        cellProps: { accessor: (row: AverageCostingGridRow) => row.colourway, fallback: '' },
      });
    }

    if (showSizeCol) {
      cols.push({
        field: 'size',
        headerName: 'Size',
        cellType: 'readonlyText',
        width: 120,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: AverageCostingGridRow) => row.size, fallback: '' },
      });
    }

    if (hideLengthCol) {
      cols.push({
        field: 'length',
        headerName: isBreakdown ? `${lengthDisplayLabel} Range` : lengthDisplayLabel,
        cellType: 'readonlyText',
        width: 120,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: AverageCostingGridRow) => row.length, fallback: '' },
      });
    }

    cols.push({
      field: 'quantity',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.AVERAGE_COSTING.QUANTITY'),
      required: true,
      cellType: 'text',
      cellRenderer: HierActionInputCell,
      width: 124,
      sortable: false,
      flex: 1,
      getCellReadOnly: (row: AverageCostingGridRow) => row.__rowType !== 'data' || statusState || !CoastingAddEditRights,
      getCellDisabled: () => onLoadDisable,
      getCellError: (row: AverageCostingGridRow) => row.qtyErr,
      getCellClass: (row: AverageCostingGridRow) => (row.qtyAmber ? 'suggested-data' : ''),
      cellProps: {
        type: 'number',
        accessor: (row: AverageCostingGridRow) => (row.__rowType === 'data' ? roundOff(row.quantity, 2, true) : row.quantity),
        getCanAdd: (row: AverageCostingGridRow) => row.__rowType === 'data' && row.__isLast && canEdit,
        getCanDelete: (row: AverageCostingGridRow) => row.__rowType === 'data' && row.canDelete && canEdit,
        onAdd: (row: AverageCostingGridRow) => handleAddRow(row.parentIndex, row.chindex, row.colourwayId),
        onDelete: (row: AverageCostingGridRow) => handleDeleteRow(row.parentIndex, row.chindex, row.rowIndex, row.colourwayId),
      },
    });

    cols.push({
      field: 'pricePerUnit',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.AVERAGE_COSTING.PRICE_PER_UNIT'),
      cellType: 'currency',
      width: 129,
      sortable: false,
      flex: 1,
      getCellReadOnly: (row: AverageCostingGridRow) => row.__rowType !== 'data' || statusState || !canEdit,
      getCellDisabled: () => !CoastingAddEditRights || onLoadDisable,
      getCellError: (row: AverageCostingGridRow) => row.priceErr,
      getCellClass: (row: AverageCostingGridRow) => (row.priceAmber ? 'suggested-data' : ''),
      cellProps: { accessor: (row: AverageCostingGridRow) => (row.__rowType === 'data' ? roundOff(row.pricePerUnit, 2, true) : row.price) },
    });

    cols.push({
      field: 'totalPrice',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.AVERAGE_COSTING.TOTAL_PRICE'),
      cellRenderer: TotalPriceTextCell,
      width: 120,
      sortable: false,
      flex: 1,
      cellProps: {
        accessor: (row: AverageCostingGridRow) =>
          row.__rowType === 'data' ? `${checkCurrency(currency, row.totalPrice) || ''} ${roundingOff(row.totalPrice, 2)}`.trim() : row.total,
      },
    });

    cols.push({
      colId: '__default',
      headerName: '',
      cellRenderer: DefaultRadioCell,
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      flex: 1,
      sortable: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      cellProps: { canEdit },
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldShowExpand,
    showSizeCol,
    hideColourCol,
    hideLengthCol,
    isBreakdown,
    averageCostingAllExpand,
    statusState,
    canEdit,
    CoastingAddEditRights,
    onLoadDisable,
    currency,
    t,
    lengthDisplayLabel,
  ]);

  return (
    <CostingCardShell title={t('DEVLOPMENT_ORDER.PRODUCT_COSTING.CORE_PRODUCT')}>
      {(fullView) => (
        <>
          <div className="costing-grid-fill" style={fullView ? undefined : { minHeight: gridHeight(Math.min(rowData.length, CORE_MAX_ROWS + 1)) }}>
            <BombiixAGGrid<AverageCostingGridRow>
              hideCellErrorMessage
              revealHeaderControlsOnHover
              sortable={false}
              showColumnMenu={false}
              suppressColumnResize
              enableFillHandle
              onFillCells={handleFillCells}
              columnDefs={columnDefs}
              rowData={rowData}
              getRowId={(row) => row.__cagId}
              onRowChange={onRowChange}
              isRowReadOnly={(row) => row.__rowType !== 'data'}
              getRowClass={(row) => {
                if (row?.__rowType === 'grandTotal') return 'cag-hier-row--total';
                if (row?.__rowType === 'summary') return 'cag-hier-row--summary';
                if (row?.__rowType === 'data' && (row?.__span ?? 0) > 1 && (row?.rowIndex ?? 0) > 0) return 'cag-hier-row--child';
                return '';
              }}
              currencySymbol={checkCurrency(currency, undefined, true)}
              height="100%"
              gridOptions={{
                suppressMovableColumns: true,
                rowHeight: 40,
                getRowHeight: (params: RowHeightParams<AverageCostingGridRow>) => {
                  const row = params?.data;
                  if (row?.__rowType === 'data' && row?.isExpanded && !row?.__showChevron) return 40;
                  return 40;
                },
              }}
            />
          </div>
          {(!Array.isArray(averageCostingInfo) || averageCostingInfo.length === 0) && (
            <p className="text-center mt-2 mb-0">{t('COSTING_TO_DO_LIST.NO_RECORD_FOUND')}</p>
          )}
        </>
      )}
    </CostingCardShell>
  );
}

export default AverageCostingTable;
